/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React from "react"
import { FlexContainer, H4, SlidingDotsLoader, Stack } from "@carrotfertility/carotene"
import { RadiusSelect } from "./RadiusSelect"
import { SidebarLocationPicker } from "./SidebarLocationPicker"
import { CarrotPartnerFilter } from "./CarrotPartnerFilter"
import { ProviderTypeFilter } from "./ProviderTypeFilter"
import { useUserContext } from "../../context/UserContext"
import { useGetHasCarrotPartner } from "../../hooks/useGetCarrotPartner"
import { Redirect } from "react-router"
import { providerFinderRootPath } from "pages/provider-finder/utils/providerFinderSearchUrl"
import {
  FertilityWraparoundMessageForSidebar,
  FertilityWraparoundMessageWhenAdditionalEmployerBenefitsForSidebar
} from "./FertilityWraparoundMessage"
import { TierInfoWhenCustomNetworksForSidebar } from "./TierInfoMessage"
import { useCompanyContext } from "../../context/CompanyContextProvider"
import Settings from "../../lib/Settings"
import { Benefit } from "pages/provider-finder/types/Benefit"
import { FormattedMessage } from "react-intl"
import { getAvailableParentIdsByBenefitConfig } from "pages/provider-finder/utils/parentIdMapping"

export function SidebarFilters({ searchLocation, benefit }: { searchLocation: string; benefit: Benefit }): JSX.Element {
  const { companyCountryCode } = useUserContext()
  const { companyId } = useCompanyContext()
  const { data, isLoading, isError } = useGetHasCarrotPartner(companyCountryCode)
  const hasCarrotPartner = data?.hasCarrotPartner
  const hasMultipleProviderTypes = getAvailableParentIdsByBenefitConfig(benefit).length > 1
  const hasFertilityWraparoundEnabled = benefit.providerFinder.showFertilityWrapAroundMessage
  const hasAdditionalEmployerBenefits = !!benefit.customizations.additionalEmployerBenefitMarkdown
  const hasCustomCoverageText = !!benefit.customizations.memberCustomCoverageExplanationMarkdown
  const networkConfigForEnv = Settings.COMPANY_IDS_WITH_TIERED_PROVIDER_NETWORK

  const showTierInfoMessage = networkConfigForEnv?.includes(parseInt(companyId, 10))

  if (isError) {
    return <Redirect to={`${providerFinderRootPath}/error`} />
  }

  return (
    <>
      {isLoading ? (
        <FlexContainer
          background="lightGrey"
          height={"120px"}
          alignItems="center"
          justifyContent="center"
          borderRadius="8px"
        >
          <SlidingDotsLoader />
        </FlexContainer>
      ) : (
        <Stack minHeight="178px" background="lightGrey" borderRadius="8px" padding="12px" spacing="huge">
          <Stack spacing="tiny">
            <H4 padding="20px 12px 8px 12px">
              <FormattedMessage defaultMessage="Location" />
            </H4>
            <SidebarLocationPicker searchLocation={searchLocation} />
            <RadiusSelect />
          </Stack>
          {showTierInfoMessage ? <TierInfoWhenCustomNetworksForSidebar showLink={hasCustomCoverageText} /> : null}
          {(hasCarrotPartner || hasMultipleProviderTypes || hasFertilityWraparoundEnabled) && (
            <Stack spacing="small">
              <H4 padding="0 12px">
                <FormattedMessage defaultMessage="Filter By" />
              </H4>
              {hasCarrotPartner && <CarrotPartnerFilter />}
              {hasMultipleProviderTypes && <ProviderTypeFilter benefit={benefit} />}
              {hasFertilityWraparoundEnabled &&
                (hasAdditionalEmployerBenefits ? (
                  <FertilityWraparoundMessageWhenAdditionalEmployerBenefitsForSidebar />
                ) : (
                  <FertilityWraparoundMessageForSidebar />
                ))}
            </Stack>
          )}
        </Stack>
      )}
    </>
  )
}
